import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  BarChart3Icon,
  BookOpenIcon,
  CalendarIcon,
  CheckCircleIcon,
  CreditCardIcon,
  Gamepad2Icon,
  Globe2Icon,
  Percent,
  TargetIcon,
  TrophyIcon,
  UsersIcon,
  ZapIcon,
  ArrowUpRightIcon,
  SparklesIcon
} from 'lucide-react';
import api from '../../services/api';
import { getDefaultAcademicYearRange } from '../../utils/dashboardDateRange';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

const QUALITY_TONES = {
  rose: {
    bar: 'bg-gradient-to-r from-rose-400 to-rose-600 shadow-sm shadow-rose-500/30',
    track: 'bg-rose-100/90 ring-1 ring-rose-200/60',
    pill: 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/80',
    value: 'text-rose-800'
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-500/25',
    track: 'bg-amber-100/90 ring-1 ring-amber-200/60',
    pill: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80',
    value: 'text-amber-900'
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm shadow-emerald-500/25',
    track: 'bg-emerald-100/90 ring-1 ring-emerald-200/60',
    pill: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200/80',
    value: 'text-emerald-900'
  }
};

function QualityBar({ label, count, percent, tone }) {
  const q = QUALITY_TONES[tone] || QUALITY_TONES.emerald;
  const w = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/80 p-3 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
      <div className="flex justify-between gap-3 text-xs">
        <span className={`inline-flex max-w-[65%] items-center rounded-lg px-2 py-0.5 font-semibold ${q.pill}`}>{label}</span>
        <span className={`shrink-0 font-bold tabular-nums ${q.value}`}>
          {fmt(count)} · {pct(percent)}
        </span>
      </div>
      <div className={`mt-2.5 h-2.5 overflow-hidden rounded-full ${q.track}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${q.bar}`}
          style={{ width: `${w}%` }}
        />
      </div>
    </div>
  );
}

const METRIC_VARIANTS = {
  emerald: {
    card: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/70 shadow-emerald-500/10 ring-emerald-500/10',
    icon: 'bg-emerald-500/15 text-emerald-700 ring-emerald-400/30',
    label: 'text-emerald-900/75',
    value: 'text-emerald-950',
    sub: 'text-emerald-800/85',
    glow: 'from-emerald-400/25'
  },
  lime: {
    card: 'border-lime-200/80 bg-gradient-to-br from-lime-50 via-white to-emerald-50/60 shadow-lime-500/10 ring-lime-500/10',
    icon: 'bg-lime-500/15 text-lime-800 ring-lime-400/30',
    label: 'text-lime-900/75',
    value: 'text-lime-950',
    sub: 'text-lime-900/80',
    glow: 'from-lime-400/25'
  },
  blue: {
    card: 'border-blue-200/80 bg-gradient-to-br from-blue-50 via-white to-sky-50/70 shadow-blue-500/10 ring-blue-500/10',
    icon: 'bg-blue-500/15 text-blue-700 ring-blue-400/30',
    label: 'text-blue-900/75',
    value: 'text-blue-950',
    sub: 'text-blue-900/80',
    glow: 'from-blue-400/25'
  },
  cyan: {
    card: 'border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-sky-50/60 shadow-cyan-500/10 ring-cyan-500/10',
    icon: 'bg-cyan-500/15 text-cyan-800 ring-cyan-400/30',
    label: 'text-cyan-950/80',
    value: 'text-cyan-950',
    sub: 'text-cyan-900/80',
    glow: 'from-cyan-400/25'
  },
  sky: {
    card: 'border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-blue-50/50 shadow-sky-500/10 ring-sky-500/10',
    icon: 'bg-sky-500/15 text-sky-800 ring-sky-400/30',
    label: 'text-sky-950/80',
    value: 'text-sky-950',
    sub: 'text-sky-900/80',
    glow: 'from-sky-400/25'
  },
  indigo: {
    card: 'border-indigo-200/80 bg-gradient-to-br from-indigo-50 via-white to-violet-50/50 shadow-indigo-500/10 ring-indigo-500/10',
    icon: 'bg-indigo-500/15 text-indigo-700 ring-indigo-400/30',
    label: 'text-indigo-950/80',
    value: 'text-indigo-950',
    sub: 'text-indigo-900/80',
    glow: 'from-indigo-400/25'
  },
  violet: {
    card: 'border-violet-200/80 bg-gradient-to-br from-violet-50 via-white to-purple-50/50 shadow-violet-500/10 ring-violet-500/10',
    icon: 'bg-violet-500/15 text-violet-700 ring-violet-400/30',
    label: 'text-violet-950/80',
    value: 'text-violet-950',
    sub: 'text-violet-900/80',
    glow: 'from-violet-400/25'
  },
  purple: {
    card: 'border-purple-200/80 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50/40 shadow-purple-500/10 ring-purple-500/10',
    icon: 'bg-purple-500/15 text-purple-800 ring-purple-400/30',
    label: 'text-purple-950/80',
    value: 'text-purple-950',
    sub: 'text-purple-900/80',
    glow: 'from-purple-400/25'
  },
  fuchsia: {
    card: 'border-fuchsia-200/80 bg-gradient-to-br from-fuchsia-50 via-white to-pink-50/40 shadow-fuchsia-500/10 ring-fuchsia-500/10',
    icon: 'bg-fuchsia-500/15 text-fuchsia-800 ring-fuchsia-400/30',
    label: 'text-fuchsia-950/80',
    value: 'text-fuchsia-950',
    sub: 'text-fuchsia-900/80',
    glow: 'from-fuchsia-400/25'
  },
  amber: {
    card: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50/50 shadow-amber-500/10 ring-amber-500/10',
    icon: 'bg-amber-500/15 text-amber-800 ring-amber-400/30',
    label: 'text-amber-950/80',
    value: 'text-amber-950',
    sub: 'text-amber-900/80',
    glow: 'from-amber-400/25'
  },
  rose: {
    card: 'border-rose-200/80 bg-gradient-to-br from-rose-50 via-white to-orange-50/40 shadow-rose-500/10 ring-rose-500/10',
    icon: 'bg-rose-500/15 text-rose-800 ring-rose-400/30',
    label: 'text-rose-950/80',
    value: 'text-rose-950',
    sub: 'text-rose-900/80',
    glow: 'from-rose-400/25'
  },
  slate: {
    card: 'border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-slate-100/50 shadow-slate-500/10 ring-slate-500/10',
    icon: 'bg-slate-500/15 text-slate-700 ring-slate-400/30',
    label: 'text-slate-700',
    value: 'text-slate-900',
    sub: 'text-slate-600',
    glow: 'from-slate-400/20'
  }
};

function MetricTile({ icon: Icon, label, value, sub, variant = 'slate' }) {
  const v = METRIC_VARIANTS[variant] || METRIC_VARIANTS.slate;
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border p-5 shadow-md ring-1 transition duration-300 hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}
    >
      <div
        className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`}
      />
      <div className="relative">
        {Icon && (
          <span className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1.5 text-3xl font-bold tabular-nums tracking-tight ${v.value}`}>{value}</p>
        {sub && <p className={`mt-1 text-xs font-semibold ${v.sub}`}>{sub}</p>}
      </div>
    </div>
  );
}

/** أنماط أقسام إحصائيات الطالب — نفس البنية البصرية */
function StatSection({ icon: Icon, title, action, border, ring, shadow, headerBg, iconGradient, titleClass, contentBg, children }) {
  return (
    <section className={`w-full overflow-hidden rounded-3xl border ${border} bg-white shadow-xl ${shadow} ring-1 ${ring}`}>
      <div
        className={`flex flex-col gap-2 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between ${headerBg}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg ring-2 ring-white/50 ${iconGradient}`}
          >
            <Icon className="h-5 w-5" />
          </span>
          <h2 className={`text-lg font-bold truncate tracking-tight ${titleClass}`}>{title}</h2>
        </div>
        {action}
      </div>
      <div className={`relative bg-gradient-to-b p-6 ${contentBg}`}>{children}</div>
    </section>
  );
}

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const defaultRange = useMemo(() => getDefaultAcademicYearRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [dailyExercises, setDailyExercises] = useState(null);
  const [flashAnzan, setFlashAnzan] = useState(null);
  const [flashCards, setFlashCards] = useState(null);
  const [competitions, setCompetitions] = useState(null);
  const [games, setGames] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [periodLabel, setPeriodLabel] = useState(null);

  const dateRef = useRef({ dateFrom, dateTo });
  dateRef.current = { dateFrom, dateTo };

  const loadDashboard = useCallback(async (rangeOverride) => {
    const { dateFrom: from, dateTo: to } = rangeOverride ?? dateRef.current;
    const queryParams = { dateFrom: from, dateTo: to };
    setLoading(true);
    const endpoints = [
      { key: 'users', path: '/dashboard/statistics/users' },
      { key: 'organization', path: '/dashboard/statistics/organization' },
      { key: 'dailyExercises', path: '/dashboard/statistics/daily-exercises' },
      { key: 'flashAnzan', path: '/dashboard/statistics/flash-anzan' },
      { key: 'flashCards', path: '/dashboard/statistics/flash-cards' },
      { key: 'competitions', path: '/dashboard/statistics/competitions' },
      { key: 'games', path: '/dashboard/statistics/games' },
      { key: 'challenges', path: '/dashboard/statistics/challenges' }
    ];

    const results = await Promise.allSettled(
      endpoints.map((e) => api.get(e.path, { params: queryParams }))
    );

    const setters = {
      users: setUsers,
      organization: setOrganization,
      dailyExercises: setDailyExercises,
      flashAnzan: setFlashAnzan,
      flashCards: setFlashCards,
      competitions: setCompetitions,
      games: setGames,
      challenges: setChallenges
    };

    results.forEach((result, i) => {
      const key = endpoints[i].key;
      if (result.status === 'fulfilled' && result.value.data?.success) {
        setters[key](result.value.data.data);
      } else {
        setters[key](null);
        if (result.status === 'rejected') {
          console.error(`Dashboard ${key}:`, result.reason);
        }
      }
    });

    const firstOk = results.find((r) => r.status === 'fulfilled' && r.value.data?.success);
    if (firstOk) {
      const d = firstOk.value.data.data;
      const p = d?.period;
      if (p?.seasonYearLabel) setPeriodLabel(p.seasonYearLabel);
      else if (p?.start && p?.end) setPeriodLabel(`${p.start} → ${p.end}`);
      else setPeriodLabel(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDashboard();
  }, [user?.id, user?.role, loadDashboard]);

  const um = users?.metrics;
  const om = organization?.metrics;
  const dm = dailyExercises?.metrics;
  const fam = flashAnzan?.metrics;
  const fcm = flashCards?.metrics;
  const cm = competitions?.metrics;
  const gm = games?.metrics;
  const chm = challenges?.metrics;

  const operationKeys = ['addition', 'subtraction', 'mixed', 'multiplication', 'division'];

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-500">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
        <header className="space-y-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200/80 bg-white/90 px-3 py-1 text-xs font-semibold text-indigo-800/90 shadow-sm ring-1 ring-indigo-500/10">
              <SparklesIcon className="h-3.5 w-3.5 text-amber-500" />
              {t('dashboard.stats.overview')}
            </div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-indigo-900 to-violet-900 bg-clip-text text-transparent">
              {t('dashboard.title')}
            </h1>
            <p className="mt-1.5 text-slate-600">
              {t('dashboard.welcome')}, {user.firstName} {user.lastName}
              {periodLabel && <span className="text-slate-400"> · {periodLabel}</span>}
            </p>
          </div>

          <div className="rounded-2xl border border-indigo-200/50 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/40 p-4 shadow-lg shadow-indigo-500/5 ring-1 ring-indigo-500/10 backdrop-blur-sm">
            <p className="mb-3 text-xs font-medium text-indigo-900/70">{t('dashboard.stats.dateFilterScope')}</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap">
              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5 text-indigo-800/80">
                  <CalendarIcon className="h-3.5 w-3.5 text-indigo-500" />
                  {t('dashboard.stats.dateFrom')}
                </span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner shadow-slate-900/5 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5 text-indigo-800/80">
                  <CalendarIcon className="h-3.5 w-3.5 text-indigo-500" />
                  {t('dashboard.stats.dateTo')}
                </span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-xl border border-slate-200/90 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-inner shadow-slate-900/5 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/30"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  const r = getDefaultAcademicYearRange();
                  setDateFrom(r.dateFrom);
                  setDateTo(r.dateTo);
                  dateRef.current = r;
                  loadDashboard(r);
                }}
                className="rounded-xl border border-slate-300/80 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-400/80 hover:bg-slate-50"
              >
                {t('dashboard.stats.resetDateRange')}
              </button>
              <button
                type="button"
                onClick={() => loadDashboard()}
                disabled={loading}
                className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 hover:from-indigo-500 hover:to-violet-500 disabled:opacity-60"
              >
                {loading ? t('common.loading') : t('dashboard.stats.applyRange')}
              </button>
            </div>
          </div>
        </header>

        {loading && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        )}

        {!loading && (
          <div className="space-y-8">
            <StatSection
              icon={UsersIcon}
              title={t('dashboard.stats.sectionUsers')}
              border="border-violet-200/50"
              ring="ring-violet-400/15"
              shadow="shadow-violet-500/10"
              headerBg="border-violet-200/40 bg-gradient-to-r from-violet-500/12 via-fuchsia-50/80 to-purple-50/50"
              iconGradient="from-violet-600 to-fuchsia-600 shadow-violet-500/35"
              titleClass="text-violet-950"
              contentBg="from-white to-violet-50/20"
            >
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalRegisteredUsers')}
                  value={fmt(um?.totalRegisteredUsers)}
                  variant="violet"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalStudents')}
                  value={fmt(um?.totalStudents)}
                  variant="blue"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalTrainers')}
                  value={fmt(um?.totalTrainers)}
                  variant="emerald"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalStaff')}
                  value={fmt(um?.totalStaff)}
                  variant="slate"
                />
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <MetricTile
                  label={t('dashboard.stats.studentsLoggedIn')}
                  value={fmt(um?.studentsLoggedInCount)}
                  sub={t('dashboard.stats.percentOfStudents', { p: pct(um?.studentsLoggedInPercentOfStudents) })}
                  variant="cyan"
                />
                <MetricTile
                  label={t('dashboard.stats.studentsActive')}
                  value={fmt(um?.studentsActiveCount)}
                  sub={t('dashboard.stats.percentOfStudents', { p: pct(um?.studentsActivePercentOfStudents) })}
                  variant="emerald"
                />
              </div>
            </StatSection>

            <StatSection
              icon={Globe2Icon}
              title={t('dashboard.stats.sectionOrganization')}
              border="border-sky-200/50"
              ring="ring-sky-400/15"
              shadow="shadow-sky-500/10"
              headerBg="border-sky-200/40 bg-gradient-to-r from-sky-500/15 via-cyan-50/70 to-blue-50/50"
              iconGradient="from-sky-500 to-cyan-600 shadow-sky-500/35"
              titleClass="text-sky-950"
              contentBg="from-white to-sky-50/20"
            >
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <MetricTile label={t('navigation.countries')} value={fmt(om?.countries)} variant="sky" />
                <MetricTile label={t('navigation.regions')} value={fmt(om?.regions)} variant="blue" />
                <MetricTile label={t('navigation.academies')} value={fmt(om?.academies)} variant="indigo" />
                <MetricTile label={t('navigation.districts')} value={fmt(om?.districts)} variant="violet" />
                <MetricTile label={t('navigation.institutions')} value={fmt(om?.institutions)} variant="purple" />
                <MetricTile label={t('navigation.departments')} value={fmt(om?.departments)} variant="fuchsia" />
              </div>
            </StatSection>

            <StatSection
              icon={BookOpenIcon}
              title={t('dashboard.stats.sectionDailyExercises')}
              border="border-emerald-200/40"
              ring="ring-emerald-500/10"
              shadow="shadow-emerald-500/10"
              headerBg="border-emerald-200/30 bg-gradient-to-r from-emerald-500/10 via-teal-50/80 to-lime-50/60"
              iconGradient="from-emerald-500 to-teal-600 shadow-emerald-500/30"
              titleClass="text-emerald-950"
              contentBg="from-white to-emerald-50/20"
              action={
                <Link
                  to="/dashboard/reports/daily-exercises-ranking"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200/80 bg-white/90 px-3 py-2 text-xs font-semibold text-emerald-800 shadow-sm ring-1 ring-emerald-500/10 hover:bg-emerald-50"
                >
                  {t('dashboard.stats.dailyExercisesRankingCta')}
                  <ArrowUpRightIcon className="h-3.5 w-3.5" />
                </Link>
              }
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <MetricTile
                  icon={BarChart3Icon}
                  label={t('dashboard.stats.totalAnswers')}
                  value={fmt(dm?.totalAnswers)}
                  variant="emerald"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalParticipants')}
                  value={fmt(dm?.totalParticipants)}
                  variant="lime"
                />
                <MetricTile
                  icon={TargetIcon}
                  label={t('dashboard.stats.totalAttempts')}
                  value={fmt(dm?.totalAttempts)}
                  variant="amber"
                />
              </div>
              {dm?.attemptQualityBuckets && (
                <div className="mt-8 space-y-3">
                  <h3 className="inline-flex rounded-lg bg-emerald-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-900 ring-1 ring-emerald-300/50">
                    {t('dashboard.stats.attemptQuality')}
                  </h3>
                  <QualityBar
                    label={t('dashboard.stats.qualityWeak')}
                    count={dm.attemptQualityBuckets.weakUnder50Percent?.count}
                    percent={dm.attemptQualityBuckets.weakUnder50Percent?.percentOfAttempts}
                    tone="rose"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityMedium')}
                    count={dm.attemptQualityBuckets.medium50To79Percent?.count}
                    percent={dm.attemptQualityBuckets.medium50To79Percent?.percentOfAttempts}
                    tone="amber"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityGood')}
                    count={dm.attemptQualityBuckets.good80PlusPercent?.count}
                    percent={dm.attemptQualityBuckets.good80PlusPercent?.percentOfAttempts}
                    tone="emerald"
                  />
                </div>
              )}
            </StatSection>

            <StatSection
              icon={ZapIcon}
              title={t('dashboard.stats.flashAnzanStats')}
              border="border-sky-200/50"
              ring="ring-sky-400/15"
              shadow="shadow-sky-500/10"
              headerBg="border-sky-200/40 bg-gradient-to-r from-sky-500/15 via-blue-50/70 to-indigo-50/50"
              iconGradient="from-sky-500 to-blue-600 shadow-sky-500/35"
              titleClass="text-sky-950"
              contentBg="from-white to-sky-50/25"
              action={
                <Link
                  to="/dashboard/flash-anzan-level-statistics"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-sky-200/80 bg-white/90 px-3 py-2 text-xs font-semibold text-sky-800 shadow-sm ring-1 ring-sky-500/10 hover:bg-sky-50"
                >
                  {t('dashboard.stats.detailedLevelStats')}
                  <ArrowUpRightIcon className="h-3.5 w-3.5" />
                </Link>
              }
            >
              <div className="grid w-full gap-4 sm:grid-cols-2">
                <MetricTile
                  icon={BarChart3Icon}
                  label={t('dashboard.stats.totalSessions')}
                  value={fmt(fam?.totalSessions)}
                  variant="sky"
                />
                <MetricTile
                  icon={CheckCircleIcon}
                  label={t('dashboard.stats.completedSessions')}
                  value={fmt(fam?.completedSessions)}
                  sub={t('dashboard.stats.percentOfTotalSessions', { p: pct(fam?.completedPercentOfTotal) })}
                  variant="cyan"
                />
              </div>

              {fam?.byOperation && (
                <div className="mt-8 overflow-hidden rounded-xl border border-sky-200/70 bg-white shadow-md ring-1 ring-sky-500/10">
                  <table className="w-full min-w-[520px] text-sm" dir="ltr">
                    <thead>
                      <tr className="bg-gradient-to-r from-sky-600 to-blue-600 text-[11px] font-bold uppercase tracking-wide text-white">
                        <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.operation')}</th>
                        <th className="py-2.5 px-2 text-end">{t('dashboard.stats.totalSessions')}</th>
                        <th className="py-2.5 px-2 text-end">{t('dashboard.stats.completedSessions')}</th>
                        <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.correctCount')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sky-100">
                      {operationKeys.map((op) => {
                        const row = fam.byOperation[op];
                        if (!row) return null;
                        return (
                          <tr key={op} className="transition-colors hover:bg-sky-50/90 even:bg-sky-50/30">
                            <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-sky-950">
                              {t(`dashboard.stats.operations.${op}`)}
                            </td>
                            <td className="py-2.5 px-2 text-end tabular-nums text-slate-800">{fmt(row.sessions)}</td>
                            <td className="py-2.5 px-2 text-end tabular-nums text-slate-800">{fmt(row.completed)}</td>
                            <td className="py-2.5 pe-4 ps-2 text-end tabular-nums text-slate-800">{fmt(row.correct)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {fam?.answerQuality && (
                <div className="mt-8 space-y-3">
                  <h3 className="inline-flex rounded-lg bg-sky-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-sky-950 ring-1 ring-sky-300/50">
                    {t('dashboard.stats.answerQualityCompleted')}
                  </h3>
                  <QualityBar
                    label={t('dashboard.stats.qualityWeak')}
                    count={fam.answerQuality.weakUnder50Percent?.count}
                    percent={fam.answerQuality.weakUnder50Percent?.percentOfCompleted}
                    tone="rose"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityMedium')}
                    count={fam.answerQuality.medium50To79Percent?.count}
                    percent={fam.answerQuality.medium50To79Percent?.percentOfCompleted}
                    tone="amber"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityGood')}
                    count={fam.answerQuality.good80PlusPercent?.count}
                    percent={fam.answerQuality.good80PlusPercent?.percentOfCompleted}
                    tone="emerald"
                  />
                </div>
              )}

              {fam?.byDifficulty && (
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  {['easy', 'medium', 'hard'].map((d) => {
                    const row = fam.byDifficulty[d];
                    if (!row) return null;
                    return (
                      <MetricTile
                        key={d}
                        label={t(`dashboard.stats.difficulty.${d}`)}
                        value={fmt(row.sessions)}
                        sub={t('dashboard.stats.successRate', { p: pct(row.successRatePercent) })}
                        variant={d === 'easy' ? 'emerald' : d === 'medium' ? 'amber' : 'rose'}
                      />
                    );
                  })}
                </div>
              )}
            </StatSection>

            <StatSection
              icon={CreditCardIcon}
              title={t('dashboard.stats.flashCardsStats')}
              border="border-violet-200/50"
              ring="ring-violet-400/15"
              shadow="shadow-violet-500/10"
              headerBg="border-violet-200/40 bg-gradient-to-r from-violet-500/12 via-indigo-50/80 to-fuchsia-50/50"
              iconGradient="from-violet-600 to-fuchsia-600 shadow-violet-500/35"
              titleClass="text-violet-950"
              contentBg="from-white to-violet-50/20"
              action={
                <Link
                  to="/dashboard/flash-cards-level-statistics"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200/80 bg-white/90 px-3 py-2 text-xs font-semibold text-violet-800 shadow-sm ring-1 ring-violet-500/10 hover:bg-violet-50"
                >
                  {t('dashboard.stats.detailedLevelStats')}
                  <ArrowUpRightIcon className="h-3.5 w-3.5" />
                </Link>
              }
            >
              <div className="grid w-full gap-4 sm:grid-cols-2">
                <MetricTile
                  icon={BarChart3Icon}
                  label={t('dashboard.stats.totalSessions')}
                  value={fmt(fcm?.totalSessions)}
                  variant="violet"
                />
                <MetricTile
                  icon={CheckCircleIcon}
                  label={t('dashboard.stats.completedSessions')}
                  value={fmt(fcm?.completedSessions)}
                  sub={t('dashboard.stats.percentOfTotalSessions', { p: pct(fcm?.completedPercentOfTotal) })}
                  variant="purple"
                />
              </div>

              {fcm?.byFlashCardType && (
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {['1', '2', '3', '4'].map((id) => {
                    const row = fcm.byFlashCardType[id];
                    if (!row) return null;
                    return (
                      <MetricTile
                        key={id}
                        label={t('dashboard.stats.flashCardTypeN', { n: id })}
                        value={fmt(row.sessions)}
                        sub={t('dashboard.stats.avgSuccess', { p: pct(row.averageSuccessPercent) })}
                        variant={['indigo', 'violet', 'purple', 'fuchsia'][Number(id) - 1] || 'indigo'}
                      />
                    );
                  })}
                </div>
              )}

              {fcm?.answerQuality && (
                <div className="mt-8 space-y-3">
                  <h3 className="inline-flex rounded-lg bg-violet-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-950 ring-1 ring-violet-300/50">
                    {t('dashboard.stats.answerQualitySessions')}
                  </h3>
                  <QualityBar
                    label={t('dashboard.stats.qualityWeak')}
                    count={fcm.answerQuality.weakUnder50Percent?.count}
                    percent={fcm.answerQuality.weakUnder50Percent?.percentOfSessions}
                    tone="rose"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityMedium')}
                    count={fcm.answerQuality.medium50To79Percent?.count}
                    percent={fcm.answerQuality.medium50To79Percent?.percentOfSessions}
                    tone="amber"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityGood')}
                    count={fcm.answerQuality.good80PlusPercent?.count}
                    percent={fcm.answerQuality.good80PlusPercent?.percentOfSessions}
                    tone="emerald"
                  />
                </div>
              )}
            </StatSection>

            <StatSection
              icon={TrophyIcon}
              title={t('dashboard.stats.sectionCompetitions')}
              border="border-cyan-200/60"
              ring="ring-cyan-400/15"
              shadow="shadow-cyan-500/10"
              headerBg="border-cyan-200/40 bg-gradient-to-r from-cyan-500/15 via-teal-50/80 to-sky-50/50"
              iconGradient="from-cyan-500 to-teal-600 shadow-cyan-500/35"
              titleClass="text-cyan-950"
              contentBg="from-white to-cyan-50/25"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <MetricTile
                  icon={TrophyIcon}
                  label={t('dashboard.stats.totalCompetitions')}
                  value={fmt(cm?.totalCompetitions)}
                  variant="cyan"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalParticipations')}
                  value={fmt(cm?.totalParticipations)}
                  variant="amber"
                />
              </div>
              {cm?.participationQualityBuckets && (
                <div className="mt-8 space-y-3">
                  <h3 className="inline-flex rounded-lg bg-cyan-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-cyan-950 ring-1 ring-cyan-300/50">
                    {t('dashboard.stats.participationQuality')}
                  </h3>
                  <QualityBar
                    label={t('dashboard.stats.qualityWeak')}
                    count={cm.participationQualityBuckets.weak?.count}
                    percent={cm.participationQualityBuckets.weak?.percent}
                    tone="rose"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityMedium')}
                    count={cm.participationQualityBuckets.medium?.count}
                    percent={cm.participationQualityBuckets.medium?.percent}
                    tone="amber"
                  />
                  <QualityBar
                    label={t('dashboard.stats.qualityGood')}
                    count={cm.participationQualityBuckets.good?.count}
                    percent={cm.participationQualityBuckets.good?.percent}
                    tone="emerald"
                  />
                </div>
              )}
            </StatSection>

            <StatSection
              icon={Gamepad2Icon}
              title={t('dashboard.stats.sectionGames')}
              border="border-amber-200/60"
              ring="ring-amber-400/15"
              shadow="shadow-amber-500/10"
              headerBg="border-amber-200/40 bg-gradient-to-r from-amber-500/15 via-orange-50/80 to-yellow-50/50"
              iconGradient="from-amber-500 to-orange-600 shadow-amber-500/35"
              titleClass="text-amber-950"
              contentBg="from-white to-amber-50/25"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <MetricTile
                  icon={Gamepad2Icon}
                  label={t('dashboard.stats.totalGamesInCatalog')}
                  value={fmt(gm?.totalGamesInCatalog)}
                  variant="amber"
                />
                <MetricTile
                  label={t('dashboard.stats.totalPlaySessions')}
                  value={fmt(gm?.totalPlaySessions)}
                  variant="fuchsia"
                />
              </div>
              {gm?.byGameId && Object.keys(gm.byGameId).length > 0 && (
                <div className="mt-8">
                  <h3 className="mb-3 inline-flex rounded-lg bg-amber-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950 ring-1 ring-amber-300/50">
                    {t('dashboard.stats.perGameBreakdown')}
                  </h3>
                  <div className="mt-4 overflow-hidden rounded-xl border border-amber-200/70 bg-white shadow-md ring-1 ring-amber-500/10">
                    <table className="w-full min-w-[420px] text-sm" dir="ltr">
                      <thead>
                        <tr className="bg-gradient-to-r from-amber-600 to-orange-600 text-[11px] font-bold uppercase tracking-wide text-white">
                          <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.gameIdCol')}</th>
                          <th className="py-2.5 px-2 text-end">{t('dashboard.stats.playSessionsCol')}</th>
                          <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {Object.entries(gm.byGameId).map(([key, row], idx) => (
                          <tr
                            key={key}
                            className={`transition-colors hover:bg-amber-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}`}
                          >
                            <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-amber-950">{row?.gameId ?? key}</td>
                            <td className="py-2.5 px-2 text-end tabular-nums font-medium text-slate-800">{fmt(row?.playSessions)}</td>
                            <td className="py-2.5 pe-4 ps-2 text-end tabular-nums font-bold text-emerald-700">
                              {pct(row?.successRatePercent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </StatSection>

            <StatSection
              icon={TargetIcon}
              title={t('dashboard.stats.sectionChallenges')}
              border="border-rose-200/60"
              ring="ring-rose-400/15"
              shadow="shadow-rose-500/10"
              headerBg="border-rose-200/40 bg-gradient-to-r from-rose-500/15 via-red-50/80 to-orange-50/40"
              iconGradient="from-rose-500 to-red-600 shadow-rose-500/35"
              titleClass="text-rose-950"
              contentBg="from-white to-rose-50/25"
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <MetricTile
                  icon={TargetIcon}
                  label={t('dashboard.stats.totalChallenges')}
                  value={fmt(chm?.totalChallenges)}
                  variant="rose"
                />
                <MetricTile
                  icon={UsersIcon}
                  label={t('dashboard.stats.totalParticipations')}
                  value={fmt(chm?.totalParticipations)}
                  variant="amber"
                />
                <MetricTile
                  icon={Percent}
                  label={t('dashboard.stats.overallSuccessRate')}
                  value={pct(chm?.overallSuccessRatePercent)}
                  variant="emerald"
                />
              </div>
              {Array.isArray(chm?.participationBySorobanLevel) && chm.participationBySorobanLevel.length > 0 && (
                <div className="mt-8">
                  <h3 className="mb-3 inline-flex rounded-lg bg-rose-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-rose-950 ring-1 ring-rose-300/50">
                    {t('dashboard.stats.submissionsByLevelTitle')}
                  </h3>
                  <div className="mt-4 overflow-hidden rounded-xl border border-rose-200/70 bg-white shadow-md ring-1 ring-rose-500/10">
                    <table className="w-full min-w-[480px] text-sm" dir="ltr">
                      <thead>
                        <tr className="bg-gradient-to-r from-rose-600 to-red-600 text-[11px] font-bold uppercase tracking-wide text-white">
                          <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.sorobanLevelCol')}</th>
                          <th className="py-2.5 px-2 text-end">{t('dashboard.stats.submissionsCol')}</th>
                          <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.percentOfTotalCol')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100">
                        {chm.participationBySorobanLevel.map((row, idx) => (
                          <tr
                            key={row.levelId ?? row.levelName}
                            className={`transition-colors hover:bg-rose-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-rose-50/40'}`}
                          >
                            <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-rose-950">
                              {row.levelName ?? row.levelId ?? '—'}
                            </td>
                            <td className="py-2.5 px-2 text-end tabular-nums text-slate-800">{fmt(row.submissions)}</td>
                            <td className="py-2.5 pe-4 ps-2 text-end tabular-nums text-slate-800">
                              {pct(row.percentOfTotalSubmissions)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {Array.isArray(chm?.byChallenge) && chm.byChallenge.length > 0 && (
                <div className="mt-8">
                  <h3 className="mb-3 inline-flex rounded-lg bg-rose-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-rose-950 ring-1 ring-rose-300/50">
                    {t('dashboard.stats.perChallengeTitle')}
                  </h3>
                  <div className="mt-4 overflow-hidden rounded-xl border border-rose-200/70 bg-white shadow-md ring-1 ring-rose-500/10">
                    <table className="w-full min-w-[480px] text-sm" dir="ltr">
                      <thead>
                        <tr className="bg-gradient-to-r from-rose-600 to-red-600 text-[11px] font-bold uppercase tracking-wide text-white">
                          <th className="py-2.5 ps-4 pe-2 text-left">{t('dashboard.stats.challengeNameCol')}</th>
                          <th className="py-2.5 px-2 text-end">{t('dashboard.stats.submissionsCol')}</th>
                          <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100">
                        {chm.byChallenge.map((row, idx) => (
                          <tr
                            key={row.challengeId ?? row.name}
                            className={`transition-colors hover:bg-rose-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-rose-50/40'}`}
                          >
                            <td className="py-2.5 ps-4 pe-2 text-left font-semibold text-rose-950">
                              {row.name ?? row.challengeId ?? '—'}
                            </td>
                            <td className="py-2.5 px-2 text-end tabular-nums text-slate-800">{fmt(row.submissions)}</td>
                            <td className="py-2.5 pe-4 ps-2 text-end tabular-nums font-bold text-emerald-700">
                              {pct(row.successRatePercent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </StatSection>
          </div>
        )}
    </div>
  );
};

export default AdminDashboard;
